import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { useCreateSheet } from '@/context/CreateSheetContext';

const FORMATS = [
  { id: 'moment',   icon: '📸', label: 'Moment',   sub: 'A thought or photo turned into a visual drop' },
  { id: 'reel',     icon: '🎬', label: 'Reel',     sub: 'Short video' },
  { id: 'rotation', icon: '🔄', label: 'Rotation', sub: 'Multi-slide portrait story' },
  { id: 'live',     icon: '🔴', label: 'Live',     sub: 'Start a Stoop' },
];

export const CreateSheet = () => {
  const { isOpen, close } = useCreateSheet();
  const { user } = useAuth();
  const navigate = useNavigate();
  const sheetRef = useRef(null);

  const lastFormat = localStorage.getItem('blvx-lastUsedFormat');
  const isPress = user?.press_tier === 'verified_press' || user?.press_tier === 'campus_reporter';

  // Close on Escape
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e) => { if (e.key === 'Escape') close(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, close]);

  // Lock body scroll when open
  useEffect(() => {
    document.body.style.overflow = isOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [isOpen]);

  const handleFormat = (formatId) => {
    close();
    navigate(`/drop?mode=${formatId}`);
  };

  const handleBackdropClick = (e) => {
    if (sheetRef.current && !sheetRef.current.contains(e.target)) {
      close();
    }
  };

  if (!isOpen) return null;

  const CARD_BASE = {
    display: 'flex', alignItems: 'center', gap: '14px',
    padding: '13px 16px', background: '#151A24',
    border: '1px solid rgba(255,255,255,0.06)', borderRadius: '12px',
    cursor: 'pointer', textAlign: 'left', width: '100%',
  };

  return (
    <div
      onClick={handleBackdropClick}
      style={{
        position: 'fixed', inset: 0, zIndex: 200,
        background: 'rgba(5,6,8,0.72)',
        backdropFilter: 'blur(4px)',
        display: 'flex', alignItems: 'flex-end',
      }}
    >
      <div
        ref={sheetRef}
        style={{
          width: '100%',
          background: '#090B10',
          borderTop: '1px solid rgba(255,255,255,0.08)',
          borderRadius: '16px 16px 0 0',
          paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 80px)',
          animation: 'sheetSlideUp 0.22s cubic-bezier(0.32,0.72,0,1)',
        }}
      >
        {/* Drag handle */}
        <div style={{ display: 'flex', justifyContent: 'center', padding: '10px 0 4px' }}>
          <div style={{ width: '36px', height: '4px', borderRadius: '2px', background: 'rgba(255,255,255,0.12)' }} />
        </div>

        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 20px 14px' }}>
          <span style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: '22px', letterSpacing: '3px', color: '#EAE0CC' }}>
            Drop Something
          </span>
          <button
            onClick={close}
            style={{
              background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.1)',
              borderRadius: '50%', width: '30px', height: '30px',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: '#72788A', fontSize: '14px', cursor: 'pointer',
            }}
          >✕</button>
        </div>

        {/* Last Drop banner — returning users only */}
        {lastFormat && (
          <div
            onClick={() => handleFormat(lastFormat)}
            style={{
              margin: '0 16px 10px',
              padding: '10px 14px',
              background: 'rgba(240,123,29,0.07)',
              border: '1px solid rgba(240,123,29,0.2)',
              borderRadius: '10px',
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              cursor: 'pointer',
            }}
          >
            <div>
              <div style={{
                fontFamily: "'Azeret Mono', monospace", fontSize: '8px',
                letterSpacing: '2px', textTransform: 'uppercase', color: '#F07B1D', marginBottom: '2px',
              }}>Last Drop</div>
              <div style={{ fontFamily: "'Instrument Sans', sans-serif", fontSize: '14px', color: '#EAE0CC', textTransform: 'capitalize' }}>
                {lastFormat}
              </div>
            </div>
            <span style={{ color: '#F07B1D', fontSize: '16px' }}>→</span>
          </div>
        )}

        {/* Format cards */}
        <div style={{ padding: '0 16px', display: 'flex', flexDirection: 'column', gap: '7px' }}>
          {FORMATS.map((fmt) => (
            <button
              key={fmt.id}
              onClick={() => handleFormat(fmt.id)}
              onPointerEnter={(e) => {
                e.currentTarget.style.background = '#1C2230';
                e.currentTarget.style.borderColor = 'rgba(240,123,29,0.22)';
              }}
              onPointerLeave={(e) => {
                e.currentTarget.style.background = '#151A24';
                e.currentTarget.style.borderColor = 'rgba(255,255,255,0.06)';
              }}
              style={CARD_BASE}
            >
              <span style={{ fontSize: '22px', flexShrink: 0, lineHeight: 1 }}>{fmt.icon}</span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: '17px', letterSpacing: '2px', color: '#EAE0CC', lineHeight: 1.1 }}>
                  {fmt.label}
                </div>
                <div style={{ fontFamily: "'Instrument Sans', sans-serif", fontSize: '12px', color: '#72788A', marginTop: '2px', lineHeight: 1.3 }}>
                  {fmt.sub}
                </div>
              </div>
              <span style={{ color: '#3A3F4E', fontSize: '16px', flexShrink: 0 }}>›</span>
            </button>
          ))}

          {/* Byline — press badge only, not in DOM otherwise */}
          {isPress && (
            <button
              onClick={() => handleFormat('byline')}
              style={{
                ...CARD_BASE,
                background: 'rgba(24,181,116,0.06)',
                border: '1px solid rgba(24,181,116,0.2)',
              }}
            >
              <span style={{ fontSize: '22px', flexShrink: 0, lineHeight: 1 }}>📰</span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: '17px', letterSpacing: '2px', color: '#EAE0CC', lineHeight: 1.1 }}>
                  Byline
                </div>
                <div style={{ fontFamily: "'Instrument Sans', sans-serif", fontSize: '12px', color: '#72788A', marginTop: '2px' }}>
                  Long-form story for HBCU.news
                </div>
              </div>
              <span style={{ fontFamily: "'Azeret Mono', monospace", fontSize: '8px', letterSpacing: '1.5px', color: '#18B574', background: 'rgba(24,181,116,0.12)', padding: '2px 7px', borderRadius: '4px', flexShrink: 0 }}>
                PRESS
              </span>
            </button>
          )}
        </div>
      </div>

      <style>{`
        @keyframes sheetSlideUp {
          from { transform: translateY(100%); }
          to   { transform: translateY(0); }
        }
      `}</style>
    </div>
  );
};
